import { useEffect } from "react";
import { useStored } from "~/core/hooks";

/**
 * How much air the workbench is drawn with.
 *
 * A viewing preference, like the filter bar and unlike the theme: it belongs to
 * this browser and never leaves the panel, so it is `localStorage` rather than
 * the parent-domain cookie the theme and the locale share with the directory.
 * Nothing on ondit.ai is a table of three hundred rows.
 *
 * The default is what the panel has always been. Compact is the answer to an
 * account that has outgrown it — it takes padding out of rows, cards and the
 * gaps between sections, and hides nothing. The tokens it switches are in
 * `app.css`; the attribute is stamped before paint by the script in
 * `index.html`, for the same reason the theme is.
 */

export type Density = "comfortable" | "compact";

/** Stamps the root element. `comfortable` clears it: the tokens already say it. */
function apply(density: Density): void {
	const root = document.documentElement;
	if (density === "compact") root.setAttribute("data-density", "compact");
	else root.removeAttribute("data-density");
}

export function useDensity(): { density: Density; setDensity: (next: Density) => void } {
	const [density, setDensity] = useStored<Density>("ondit.density", "comfortable");
	// The inline script has already stamped the attribute before paint; this
	// keeps it true after a change.
	useEffect(() => apply(density), [density]);
	return { density, setDensity };
}
